import { FunctionComponent, useCallback, useMemo } from "react";

import {
  EdrObservationsMapLayer as EdrObservationsLayerConfig,
  EdrObservationsMapLayerDataLocationsInner as EdrObservationsLayerLocation,
} from "@/lib/api";
import { TimeRangeDimensionValue } from "@/lib/dimensions/time-range-dimension";
import { LayerSettingValueSet } from "@/lib/map-layers/layer-settings";
import OverlayControl from "@/features/shared/widgets/map/controls/overlay-control";
import useSelectedMarker from "@/features/shared/widgets/map/settings/use-selected-marker";

import EdrObservationsMarker from "./edr-observations-marker";
import LocationOverlayControl from "./location-overlay-control";

interface EdrObservationsMarkersProps {
  layer: EdrObservationsLayerConfig;
  locations: EdrObservationsLayerLocation[];
  currentTime: TimeRangeDimensionValue;
  currentSettingValues: LayerSettingValueSet;
}

const EdrObservationsMarkers: FunctionComponent<EdrObservationsMarkersProps> = ({
  locations,
  layer,
  currentTime,
  currentSettingValues,
}) => {
  const { selectedMarker, setSelectedMarker, resetSelectedMarker } = useSelectedMarker();

  const handleSelect = useCallback(
    (location: EdrObservationsLayerLocation) => {
      setSelectedMarker(location.id, layer.id);
    },
    [setSelectedMarker, layer],
  );

  const selectedLocation = useMemo(() => {
    if (!selectedMarker) return undefined;
    if (selectedMarker.layerId !== layer.id) return undefined;

    return locations.find((location) => location.id === selectedMarker.value);
  }, [locations, layer, selectedMarker]);

  return (
    <>
      {locations.map((location) => (
        <EdrObservationsMarker
          key={location.id}
          location={location}
          selected={location.id === selectedLocation?.id}
          onSelect={handleSelect}
          currentTime={currentTime}
          currentSettingValues={currentSettingValues}
        />
      ))}
      {selectedLocation && (
        <OverlayControl>
          <LocationOverlayControl
            location={selectedLocation}
            layer={layer}
            currentTime={currentTime}
            handleClose={resetSelectedMarker}
          />
        </OverlayControl>
      )}
    </>
  );
};

export default EdrObservationsMarkers;
